import * as React from "react";
import { useState, useEffect } from "react";
import { ProductTile } from "@src/stories/ecommerce/Product/ProductTile";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { LoadingSpinner } from "@arctheme-components/elements/LoadingSpinner/LoadingSpinner";
import { getSearchProvider } from "@src/providers/Search";
import { useSearch } from "@src/contexts/SearchProvider";
import { useSearchSettings } from "@src/contexts/SearchSettingsContext";

export const HeaderSearchResults = ({ settings }) => {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useMobileBreakpoint(settings?.mobile_breakpoint || 1024);
  
  // Get global search settings
  const { searchSettings, updateSearchSettings } = useSearchSettings();

  const {
    columns_mobile = 1,
    columns_desktop = 2,
    column_gap_mobile = 16,
    column_gap_desktop = 24,
    row_gap_mobile = 16,
    row_gap_desktop = 24,
    max_results = 6,
    id,
  } = settings?.section || settings;

  useEffect(() => {
    const handleHeaderSearchRequest = (event) => {
      // console.log("Header search request received:", event.detail);
      if (event.detail.type === "header-search") {
        const searchQuery = event.detail.query || "";
        setQuery(searchQuery);
        
        if (searchQuery.length > 0) {
          setLoading(true);
          setIsVisible(true);
          
          // Show spinner for header search
          window.dispatchEvent(
            new CustomEvent("show-spinner", {
              detail: {
                id: "spinner-header-search",
              },
            })
          );
        } else {
          setIsVisible(false);
          setProducts([]);
          setLoading(false);
        }
      }
    };

    const handleHeaderSearchResults = (event) => {
      if (event.detail.type === "header-search") {
        const { products, query, filters: searchFilters } = event.detail;
        setQuery(query);
        setProducts(products || []);
        setLoading(false);
        if (searchFilters) setFilters(searchFilters);
        
        // Hide spinner with slight delay
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("hide-spinner", {
              detail: {
                id: "spinner-header-search",
              },
            })
          );
        }, 100);
      }
    };

    const handleSearchProductsResults = (event) => {
      if (event.detail.type === "header-search") {
        const { products, query, filters: searchFilters } = event.detail;
        setQuery(query);
        setProducts(products || []);
        setLoading(false);
        if (searchFilters) setFilters(searchFilters);

        // Hide spinner with slight delay
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("hide-spinner", {
              detail: {
                id: "spinner-header-search",
              },
            })
          );
        }, 100);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setIsVisible(false);
        setQuery("");
        setProducts([]);
      }
    };

    const handleClickOutside = (event) => {
      // Hide search results when clicking outside
      const searchContainer = event.target.closest('.header-search-results');
      const searchInput = event.target.closest('.search-input, .header-search');
      
      if (!searchContainer && !searchInput) {
        setIsVisible(false);
      }
    };

    // Set up event listeners
    window.addEventListener("searchrequest", handleHeaderSearchRequest);
    window.addEventListener("header-search-results", handleHeaderSearchResults);
    window.addEventListener("searchrequest-products-complete", handleSearchProductsResults);
    window.addEventListener("filter-list", (event: any) => {
      if (event.detail.type === "header-search") {
        if (event.detail.filters) {
          setFilters(event.detail.filters);
        }
      }
    });
    window.addEventListener("keydown", handleEscapeKey);
    document.addEventListener("click", handleClickOutside);
    
    // Update global search settings if not yet initialized
    if (!searchSettings.initialized) {
      updateSearchSettings({
        search_system: settings.search_system || 'shopify',
        settings: settings
      });
    }

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener("header-search-request", handleHeaderSearchRequest);
      window.removeEventListener("header-search-results", handleHeaderSearchResults);
      window.removeEventListener("searchrequest-products-complete", handleSearchProductsResults);
      window.removeEventListener("keydown", handleEscapeKey);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [settings, searchSettings.initialized, updateSearchSettings]);

  // Determine which search provider to use - use global settings if available
  const providerName = searchSettings.initialized ? 
    searchSettings.search_system : 
    settings.search_system || 'shopify';
    
  const SearchProviderComponent = getSearchProvider(providerName) as any;

  // Don't render anything if not visible or no query
  if (!isVisible || !query) {
    return null;
  }

  return (
    <SearchProviderComponent 
      settings={searchSettings.initialized ? searchSettings.settings : settings} 
      searchType="header"
    >
      <HeaderSearchResultsWithProvider 
        products={products} 
        filters={filters}
        query={query}
        loading={loading}
        settings={settings}
        isMobile={isMobile}
        columns={{
          mobile: columns_mobile,
          desktop: columns_desktop,
          gapMobile: column_gap_mobile,
          gapDesktop: column_gap_desktop,
          rowGapMobile: row_gap_mobile,
          rowGapDesktop: row_gap_desktop
        }}
        maxResults={max_results}
        id={id}
      />
    </SearchProviderComponent>
  );
};

// Internal component that uses the search provider context
function HeaderSearchResultsWithProvider({ 
  products, 
  filters,
  query,
  loading,
  settings, 
  isMobile,
  columns,
  maxResults,
  id
}) {
  // Access the search provider context, including initialization status
  const search = useSearch();
  
  useEffect(() => {
    // Check if the provider is initialized
    if (search.isInitialized && query) {
      // console.log("Header search provider initialized with query:", query);
    }
  }, [search.isInitialized, query]);

  // Limit products to maxResults
  const limitedProducts = products.slice(0, maxResults);

  return (
    <div 
      className="header-search-results absolute top-full left-0 right-0 bg-white border border-gray-200 shadow-lg rounded-b-lg z-50 max-h-96 overflow-y-auto"
      data-search-provider={search.settings?.search_system}
    >
      <LoadingSpinner id="spinner-header-search" />
      
      {loading && (
        <div className="p-4 text-center text-gray-500">
          Searching...
        </div>
      )}

      {!loading && limitedProducts.length > 0 && (
        <>
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-sm font-medium text-gray-900">
              Search Results for "{query}"
            </h3>
          </div>
          
          <div
            className={`grid grid-cols-${columns.mobile} lg:grid-cols-${columns.desktop} 
              gap-x-[${columns.gapMobile}px] lg:gap-x-[${columns.gapDesktop}px] 
              gap-y-[${columns.rowGapMobile}px] lg:gap-y-[${columns.rowGapDesktop}px] 
              p-4`}
            data-id={id}
          >
            {limitedProducts.map((product, index) => (
              <div key={index} className="header-search-tile">
                <ProductTile 
                  product={product} 
                  siblings={undefined}
                  settings={{
                    ...settings,
                    compact: true, // Use compact mode for header
                    hideQuickShop: true // Hide quick shop in header results
                  }}
                  enableWishlist={settings.enableWishlist || false}
                  showPrice={true}
                  showQuickAdd={false}
                />
              </div>
            ))}
          </div>
          
          {products.length > maxResults && (
            <div className="p-4 border-t border-gray-100 text-center">
              <a 
                href={`/search?q=${encodeURIComponent(query)}`}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                View all {products.length} results
              </a>
            </div>
          )}
        </>
      )}

      {!loading && limitedProducts.length === 0 && query && (
        <div className="p-8 text-center">
          <p className="text-gray-500 mb-2">No results found for "{query}"</p>
          <p className="text-sm text-gray-400">
            Try adjusting your search or browse our categories
          </p>
        </div>
      )}
    </div>
  );
}

export default HeaderSearchResults;